<template>
  <div ref="rootEl">
    <div
      v-if="shortTermApplication"
    >
      <div class="row gx-2">
        <div
          v-if="!exchangeTemplateDetails && activityType.requiresSpecialisation"
          class="mb-2 col-md-6"
        >
          <label
            for="specialisationId"
            class="form-label required"
          >
            Specialisation
          </label>
          <select
            id="specialisationId"
            v-model="shortTermApplication.specialisationId"
            class="form-select form-select-sm"
          >
            <option
              v-for="spec of specialisations"
              :key="spec.id"
              :value="spec.id"
            >
              {{ spec.name }}
            </option>
          </select>
        </div>
        <div class="mb-2 col-md-6">
          <label
            for="functionId"
            class="form-label required"
          >
            Function
          </label>
          <select
            id="functionId"
            v-model="shortTermApplication.functionId"
            class="form-select form-select-sm"
            data-active-field-tag="true"
            @change="onFunctionChanged"
          >
            <option :value="null" />
            <option
              v-for="funct of functions"
              :key="funct.id"
              :value="funct.id"
              :data-active-field-tags="funct.activeFieldTag"
            >
              {{ funct.name }}
            </option>
          </select>
        </div>
        <div
          v-if="requireCourtStaffWorkplace"
          class="mb-2 col-md-6"
        >
          <label
            for="courtStaffWorkplaceId"
            class="form-label required"
          >
            Court staff workplace
          </label>
          <select
            id="courtStaffWorkplaceId"
            v-model="shortTermApplication.courtStaffWorkplaceId"
            class="form-select form-select-sm"
            @change="onCourtStaffWorkplaceChanged"
          >
            <option :value="null" />
            <option
              v-for="workplace of courtStaffWorkplaces"
              :key="workplace.id"
              :value="workplace.id"
            >
              {{ workplace.name }}
            </option>
          </select>
        </div>
      </div>
      <div
        v-if="exchangeTemplateDetails"
        class="row gx-2"
      >
        <div class="mb-2 col-md-6">
          <label
            for="activityCategoryId"
            class="form-label required"
          >
            Category
          </label>
          <select
            id="activityCategoryId"
            v-model="shortTermApplication.activityCategoryId"
            class="form-select form-select-sm"
            :disabled="!canManageCategory"
            @change="onCategoryChanged"
          >
            <option :value="null" />
            <option
              v-for="category of availableCategories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
        </div>
        <div
          v-if="availableSpecialisations.length"
          class="mb-2 col-md-6"
        >
          <label
            for="exchangeTemplateSpecialisationId"
            class="form-label required"
          >
            Specialisation
          </label>
          <select
            id="exchangeTemplateSpecialisationId"
            v-model="shortTermApplication.specialisationId"
            class="form-select form-select-sm"
            @change="onSpecialisationChanged"
          >
            <option :value="null" />
            <option
              v-for="specialisation of availableSpecialisations"
              :key="specialisation.id"
              :value="specialisation.id"
            >
              {{ specialisation.name }}
            </option>
          </select>
        </div>
      </div>
      <div
        v-if="subSpecialisationSelectionVisible"
        class="row gx-2"
      >
        <fieldset class="mb-2 col-md-6">
          <legend class="col-form-label">
            Sub-specialisations
          </legend>
          <div
            v-for="subSpecialisation of availableSubSpecialisations"
            :key="subSpecialisation.id"
            class="form-check"
          >
            <input
              :id="`admin-sub-specialisation-${subSpecialisation.id}`"
              v-model="shortTermApplication.subSpecialisationIds"
              :value="subSpecialisation.id"
              :disabled="subSpecialisationLimitReached && !shortTermApplication.subSpecialisationIds.includes(subSpecialisation.id)"
              type="checkbox"
              class="form-check-input"
            >
            <label
              class="form-check-label"
              :for="`admin-sub-specialisation-${subSpecialisation.id}`"
            >
              {{ subSpecialisation.name }}
            </label>
          </div>
          <div class="form-check">
            <input
              id="admin-other-sub-specialisation-selected"
              v-model="otherSubSpecialisationSelected"
              :disabled="subSpecialisationLimitReached && !otherSubSpecialisationSelected"
              type="checkbox"
              class="form-check-input"
            >
            <label class="form-check-label" for="admin-other-sub-specialisation-selected">Other</label>
          </div>
        </fieldset>
        <div v-if="otherSubSpecialisationSelected" class="mb-2 col-md-6">
          <label
            for="adminOtherSubSpecialisation"
            class="form-label"
          >
            Other sub-specialisation
          </label>
          <input
            id="adminOtherSubSpecialisation"
            v-model="shortTermApplication.otherSubSpecialisation"
            type="text"
            class="form-control form-control-sm"
            required
          >
        </div>
      </div>
      <!-- Supporting document uploads are temporarily disabled.
      <div
        v-if="supportingDocumentVisible"
        class="row gx-2"
      >
        <div class="mb-2 col-md-6">
          <label class="form-label">Supporting document</label>
          <upload-field
            v-model="shortTermApplication.supportingDocument"
            :upload-url="supportingDocumentUploadUrl"
            input-group-class="input-group-sm"
          />
        </div>
      </div>
      -->
    </div>
    <div class="mt-2 choices-list">
      <ospe-exchange-application-choice-card
        title="First choice"
        :idx="1"
        v-model:choice="firstChoice"
        @change-choice="showSelectExchangeModal(0)"
        @remove-choice="showDeleteExchangePrompt(0)"
      />
      <button
        v-if="addSecondChoiceVisible"
        class="btn btn-sm btn-success"
        @click.prevent="showSelectExchangeModal(1)"
      >
        Add second choice
      </button>
      <ospe-exchange-application-choice-card
        v-if="secondChoice"
        title="Second choice"
        :idx="2"
        v-model:choice="secondChoice"
        @change-choice="showSelectExchangeModal(1)"
        @remove-choice="showDeleteExchangePrompt(1)"
      />
      <button
        v-if="addThirdChoiceVisible"
        class="btn btn-sm btn-success"
        @click.prevent="showSelectExchangeModal(2)"
      >
        Add third choice
      </button>
      <ospe-exchange-application-choice-card
        v-if="thirdChoice"
        title="Third choice"
        :idx="3"
        v-model:choice="thirdChoice"
        @change-choice="showSelectExchangeModal(2)"
        @remove-choice="showDeleteExchangePrompt(2)"
      />
    </div>

    <div
      v-if="otherCountryAllocation && shortTermApplication"
      class="row gx-2 mt-4"
    >
      <div class="mb-2 col-12">
        <label class="form-label required">I accept to be allocated in any other country</label>
        <div>
          <div class="form-check form-check-inline">
            <input
              :id="`other-country-allocation-yes`"
              v-model="shortTermApplication.allocateToOtherCountry"
              :value="true"
              type="radio"
              class="form-check-input"
            >
            <label
              class="form-check-label"
              :for="`other-country-allocation-yes`"
            >
              Yes
            </label>
          </div>
          <div class="form-check form-check-inline">
            <input
              :id="`other-country-allocation-no`"
              v-model="shortTermApplication.allocateToOtherCountry"
              :value="false"
              type="radio"
              class="form-check-input"
            >
            <label
              class="form-check-label"
              :for="`other-country-allocation-no`"
            >
              No
            </label>
          </div>
        </div>
      </div>
    </div>

    <teleport
      to="#vue-portal-target"
      append
    >
      <remote-modal
        ref="selectExchangeModal"
        modal-title="Select exchange"
        modal-class="modal-tabular"
        modal-dialog-class="modal-xl"
        loading-message="Loading exchanges ..."
        :loading-indicator-in-footer="false"
        :hide-content-when-loading="true"
        @details-loaded="onSelectExchangesLoaded"
      >
        <template
          v-if="selectFromExchangesListLoaded"
          #modal-content
        >
          <div
            v-if="availableExchangesList.length > 0"
            class="table-wrapper"
          >
            <table class="table table-hover table-sm">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Type</th>
                  <th>Language</th>
                  <th>Public</th>
                  <th>Available places</th>
                  <th>Length</th>
                  <th>Additional information</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="exchange of availableExchangesList"
                  :key="exchange.id"
                >
                  <td>{{ exchange.countryName }}</td>
                  <td>{{ exchange.exchangeType }}</td>
                  <td>{{ exchange.languages }}</td>
                  <td>{{ exchange.functions }}</td>
                  <td>{{ exchange.availablePlaces }}</td>
                  <td>{{ exchange.duration }}</td>
                  <td>{{ exchange.additionalInformation }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-info"
                      @click.prevent="selectExchange(exchange, exchangeSelectionIdx)"
                    >
                      Select
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-else
            class="alert alert-warning"
          >
            No exchanges found for selected criteria
          </div>
        </template>
        <template #modal-footer>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </template>
      </remote-modal>
      <modal-alert
        ref="modalAlert"
        close-button-title="Close"
      />
      <modal-confirmation-rails
        ref="removeChoiceConfirmationModal"
        modal-title="Remove choice?"
        @confirmed="onRemoveChoiceConfirmed"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import {computed, nextTick, onMounted, ref, useTemplateRef, watch} from 'vue'
import Jsona from 'jsona'
import OspeExchangeApplicationChoiceCard from './ospe-exchange-application-choice-card.vue'
import RemoteModal from '../remote-modal.vue'
import ModalAlert from '../modal-alert.vue'
import ModalConfirmationRails from '../modal-confirmation-rails.vue'
// Supporting document uploads are temporarily disabled.
// import UploadField from '../upload-field.vue'
import type {
  IdNamePair,
  OspeActivityType,
  OspeCourtStaffWorkplace,
  OspeFunction,
  OspeShortTermAvailableCategory,
  OspeShortTermAvailableFunction,
  OspeShortTermAvailableSpecialisation,
  OspeSpecialisation,
} from '@/js/ospe-public/types'
import type {OspeExchangeChoice, OspeShortTermApplication} from '@/js/ospe-types'
import {OspeActivityType as OspeActivityTypeEnum} from '../../ospe-types'
import {updateFieldsByTags} from '@/js/site-utils'

interface ExchangeData {
  activityType: OspeActivityType;
  availableFunctions?: OspeShortTermAvailableFunction[];
  canManageCategory?: boolean;
  supportingDocumentUploadUrl?: string;
}

const props = defineProps({
  availableChoicesUrl: {type: String, default: ''},
})

const shortTermApplication = ref<OspeShortTermApplication | null>(null)
const activityType = ref<OspeActivityType>({
  id: 0,
  name: '',
  requiresSpecialisation: false,
  functions: null,
  specialisations: null,
  courtStaffWorkplaces: null,
  otherCountryAllocation: false,
})
const exchangeSelectionIdx = ref(0)
const selectFromExchangesList = ref<OspeExchangeChoice[]>([])
const selectFromExchangesListLoaded = ref(false)
const applicationDataEl = ref<HTMLInputElement | null>(null)
const availableFunctions = ref<OspeShortTermAvailableFunction[]>([])
const canManageCategory = ref(true)
const supportingDocumentUploadUrl = ref('')

const rootEl = useTemplateRef<HTMLElement>('rootEl')
const selectExchangeModal = useTemplateRef<InstanceType<typeof RemoteModal>>('selectExchangeModal')
const modalAlert = useTemplateRef<InstanceType<typeof ModalAlert>>('modalAlert')
const removeChoiceConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>('removeChoiceConfirmationModal')

const exchangeTemplateDetails = computed((): boolean => {
  return activityType.value.id === OspeActivityTypeEnum.et
})

const requireSpecialisation = computed((): boolean => {
  return exchangeTemplateDetails.value
    ? availableSpecialisations.value.length > 0
    : activityType.value.requiresSpecialisation
})

const hasFunction = computed((): boolean => {
  return shortTermApplication.value != null && shortTermApplication.value.functionId !== null
})

const firstChoice = computed({
  get(): OspeExchangeChoice | null {
    return shortTermApplication.value?.firstChoice ?? null
  },
  set(choice: OspeExchangeChoice | null): void {
    if (shortTermApplication.value) {
      shortTermApplication.value.firstChoice = choice
    }
  },
})

const secondChoice = computed({
  get(): OspeExchangeChoice | null {
    return shortTermApplication.value?.secondChoice ?? null
  },
  set(choice: OspeExchangeChoice | null): void {
    if (shortTermApplication.value) {
      shortTermApplication.value.secondChoice = choice
    }
  },
})

const thirdChoice = computed({
  get(): OspeExchangeChoice | null {
    return shortTermApplication.value?.thirdChoice ?? null
  },
  set(choice: OspeExchangeChoice | null): void {
    if (shortTermApplication.value) {
      shortTermApplication.value.thirdChoice = choice
    }
  },
})

const addSecondChoiceVisible = computed((): boolean => {
  return firstChoice.value != null && secondChoice.value == null
})

const addThirdChoiceVisible = computed((): boolean => {
  return firstChoice.value != null && secondChoice.value != null && thirdChoice.value == null
})

const functions = computed((): OspeFunction[] => {
  if (exchangeTemplateDetails.value) {
    return availableFunctions.value.map((fn) => ({
      id: fn.id,
      name: fn.name,
      courtStaff: fn.courtStaffWorkplaces.length > 0,
      activeFieldTag: fn.courtStaffWorkplaces.length > 0 ? 'cs' : undefined,
    }))
  }

  if (activityType.value.requiresSpecialisation) {
    // get functions from the selected specialisations
    if (Array.isArray(activityType.value.specialisations) && shortTermApplication.value) {
      const app = shortTermApplication.value
      const specialisation = activityType.value.specialisations.find(spec => spec.id == app.specialisationId)
      if (specialisation) {
        return specialisation.functions
      }
    }
  } else if (Array.isArray(activityType.value.functions)) {
    return activityType.value.functions
  }
  return []
})

const courtStaffWorkplaces = computed((): OspeCourtStaffWorkplace[] => {
  if (exchangeTemplateDetails.value) {
    return selectedAvailableFunction.value?.courtStaffWorkplaces || []
  }

  if (Array.isArray(activityType.value.courtStaffWorkplaces)) {
    return activityType.value.courtStaffWorkplaces
  }
  return []
})

const specialisations = computed((): OspeSpecialisation[] => {
  if (activityType.value.requiresSpecialisation && Array.isArray(activityType.value.specialisations)) {
    return activityType.value.specialisations
  }
  return []
})

const selectedFunction = computed((): OspeFunction | null => {
  return functions.value.find(fn => fn.id == shortTermApplication.value?.functionId) || null
})

const selectedAvailableFunction = computed((): OspeShortTermAvailableFunction | null => {
  return availableFunctions.value.find(fn => fn.id == shortTermApplication.value?.functionId) || null
})

const requireCourtStaffWorkplace = computed((): boolean => {
  if (exchangeTemplateDetails.value) {
    return courtStaffWorkplaces.value.length > 0
  }

  return selectedFunction.value?.courtStaff === true
})

const availableCategories = computed((): OspeShortTermAvailableCategory[] => {
  let categories = selectedAvailableFunction.value?.categories || []
  if (requireCourtStaffWorkplace.value) {
    const workplaceId = shortTermApplication.value?.courtStaffWorkplaceId
    if (workplaceId === null || workplaceId === undefined) {
      return []
    }
    categories = categories.filter(category => category.courtStaffWorkplaceIds.includes(workplaceId))
  }
  return categories
})

const selectedCategory = computed((): OspeShortTermAvailableCategory | null => {
  return availableCategories.value.find(
    category => category.id == shortTermApplication.value?.activityCategoryId,
  ) || null
})

const availableSpecialisations = computed((): OspeShortTermAvailableSpecialisation[] => {
  let items = selectedCategory.value?.specialisations || []
  if (requireCourtStaffWorkplace.value) {
    const workplaceId = shortTermApplication.value?.courtStaffWorkplaceId
    if (workplaceId === null || workplaceId === undefined) {
      return []
    }
    items = items.filter(specialisation => specialisation.courtStaffWorkplaceIds.includes(workplaceId))
  }
  return items
})

const selectedAvailableSpecialisation = computed((): OspeShortTermAvailableSpecialisation | null => {
  return availableSpecialisations.value.find(
    specialisation => specialisation.id == shortTermApplication.value?.specialisationId,
  ) || null
})

const availableSubSpecialisations = computed((): IdNamePair[] => {
  return selectedAvailableSpecialisation.value?.subSpecialisations || []
})

const subSpecialisationSelectionVisible = computed((): boolean => {
  return selectedAvailableSpecialisation.value?.withSubSpecialisations === true ||
    availableSubSpecialisations.value.some(value => shortTermApplication.value?.subSpecialisationIds.includes(value.id)) ||
    otherSubSpecialisationSelected.value
})

const otherSubSpecialisationSelected = computed({
  get: (): boolean => shortTermApplication.value?.otherSubSpecialisation != null,
  set: (selected: boolean): void => {
    if (shortTermApplication.value) {
      shortTermApplication.value.otherSubSpecialisation = selected ? '' : null
    }
  },
})

const subSpecialisationLimitReached = computed((): boolean => {
  return (shortTermApplication.value?.subSpecialisationIds.length || 0) +
    (otherSubSpecialisationSelected.value ? 1 : 0) >= 3
})

const supportingDocumentVisible = computed((): boolean => {
  return selectedAvailableSpecialisation.value?.allowSupportingDocuments === true
})

const otherCountryAllocation = computed((): boolean => {
  return activityType.value.otherCountryAllocation && !subSpecialisationSelectionVisible.value
})

const availableExchangesList = computed((): OspeExchangeChoice[] => {
  const selectedPlaceIds = [
    firstChoice.value?.id,
    secondChoice.value?.id,
    thirdChoice.value?.id,
  ].filter(v => v != null).map(v => v?.toString() || '')

  return selectFromExchangesList.value.filter((f) => {
    if (f.activityId && selectedPlaceIds.includes(f.id.toString())) {
      return false
    }

    return true
  })
})

const getFormEl = (): HTMLFormElement | null => {
  return rootEl.value?.closest('form') ?? null
}

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(shortTermApplication.value)
  }
}

const resetSpecialisationDetails = (): void => {
  if (!shortTermApplication.value) return

  shortTermApplication.value.subSpecialisationIds = []
  shortTermApplication.value.otherSubSpecialisation = null
  if (subSpecialisationSelectionVisible.value) {
    shortTermApplication.value.allocateToOtherCountry = null
  }
  if (!supportingDocumentVisible.value) {
    shortTermApplication.value.supportingDocument = {id: '', filename: '', url: ''}
  }
}

const onSpecialisationChanged = (): void => {
  resetSpecialisationDetails()
}

const onCategoryChanged = (): void => {
  if (!shortTermApplication.value) return

  const selectedId = shortTermApplication.value.specialisationId
  if (!availableSpecialisations.value.some(specialisation => specialisation.id == selectedId)) {
    shortTermApplication.value.specialisationId = null
    resetSpecialisationDetails()
  }
}

const onCourtStaffWorkplaceChanged = (): void => {
  if (!shortTermApplication.value) {
    return
  }

  // available categories are not known until the workplace is selected
  if (requireCourtStaffWorkplace.value && shortTermApplication.value.courtStaffWorkplaceId == null) {
    return
  }

  // category preset from the activity can not be selected again, so keep it
  const selectedId = shortTermApplication.value.activityCategoryId
  if (canManageCategory.value && !availableCategories.value.some(category => category.id == selectedId)) {
    shortTermApplication.value.activityCategoryId = null
  }
  onCategoryChanged()
}

const onFunctionChanged = (): void => {
  if (!shortTermApplication.value) return

  const workplaceId = shortTermApplication.value.courtStaffWorkplaceId
  if (!courtStaffWorkplaces.value.some(workplace => workplace.id == workplaceId)) {
    shortTermApplication.value.courtStaffWorkplaceId = null
  }
  onCourtStaffWorkplaceChanged()
}

watch(shortTermApplication, () => {
  storeApplicationData()

  nextTick(() => {
    const formEl = getFormEl()
    if (formEl) {
      updateFieldsByTags(formEl)
    }
  })
}, {deep: true})

onMounted(() => {
  const exchangesDataEl = document.querySelector('script[ref="exchangeSelectionData"]')
  if (exchangesDataEl instanceof HTMLScriptElement && exchangesDataEl.type == 'application/json') {
    try {
      const exchangeData: ExchangeData = JSON.parse(exchangesDataEl.innerText)
      if (exchangeData.activityType) {
        activityType.value = exchangeData.activityType
      }
      if (Array.isArray(exchangeData.availableFunctions)) {
        availableFunctions.value = exchangeData.availableFunctions
      }
      canManageCategory.value = exchangeData.canManageCategory !== false
      supportingDocumentUploadUrl.value = exchangeData.supportingDocumentUploadUrl || ''
    } catch { /* empty */
    }
  }

  const dataEl = document.querySelector('input[ref="applicationDataJson"]')
  if (dataEl instanceof HTMLInputElement) {
    applicationDataEl.value = dataEl
    try {
      const applicationData = JSON.parse(applicationDataEl.value.value) as OspeShortTermApplication
      applicationData.activityCategoryId ??= null
      applicationData.subSpecialisationIds = Array.isArray(applicationData.subSpecialisationIds)
        ? applicationData.subSpecialisationIds
        : []
      applicationData.otherSubSpecialisation ??= null
      if (!applicationData.supportingDocument || typeof applicationData.supportingDocument !== 'object') {
        applicationData.supportingDocument = {id: '', filename: '', url: ''}
      }
      shortTermApplication.value = applicationData
    } catch { /* empty */
    }
  }
})

const showSelectExchangeModal = (choiceIdx: number): void => {
  if (activityType.value.id == 0) {
    // no activity type
    return
  }

  if (!shortTermApplication.value) {
    return
  }

  if (!hasFunction.value) {
    modalAlert.value?.show('No function selected', 'Please select function', 'danger')
    return
  }

  if (exchangeTemplateDetails.value && !shortTermApplication.value.activityCategoryId) {
    modalAlert.value?.show('No category selected', 'Please select category', 'danger')
    return
  }

  if (requireSpecialisation.value && !shortTermApplication.value.specialisationId) {
    modalAlert.value?.show('No specialisation selected', 'Please select specialisation', 'danger')
    return
  }

  selectFromExchangesListLoaded.value = false
  exchangeSelectionIdx.value = choiceIdx

  const selectedSpecialisationId = shortTermApplication.value.specialisationId
  const selectedCategoryId = shortTermApplication.value.activityCategoryId

  let selectedCourtStaffWorkplaceId: number | null = null
  if (requireCourtStaffWorkplace.value) {
    selectedCourtStaffWorkplaceId = shortTermApplication.value.courtStaffWorkplaceId
  }

  const functionId = shortTermApplication.value.functionId
  selectExchangeModal.value?.showModal(props.availableChoicesUrl, {
    activity_type_id: activityType.value.id,
    specialisation_id: selectedSpecialisationId,
    category_id: selectedCategoryId,
    function_id: functionId,
    court_staff_workplace_id: selectedCourtStaffWorkplaceId,
  })
}

const showDeleteExchangePrompt = (exchangeIdx: number): void => {
  removeChoiceConfirmationModal.value?.show(null, {
    title: 'Remove choice?',
    message: 'Are you sure you want to remove this choice?',
    type: 'danger',
    cancelActionTitle: 'Cancel',
    confirmActionTitle: 'Remove',
    confirmationCode: null,
    context: exchangeIdx,
  })
}

const onSelectExchangesLoaded = (data: any): void => {
  selectFromExchangesListLoaded.value = true
  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    selectFromExchangesList.value = loadedData as OspeExchangeChoice[]
  }
}

const prepareExchange = (exchange: OspeExchangeChoice, previousExchange: OspeExchangeChoice | null): OspeExchangeChoice => {
  // copy exchange
  const nextExchange = JSON.parse(JSON.stringify(exchange)) as OspeExchangeChoice

  if (previousExchange) {
    if (previousExchange.exchangeTypeId) {
      const typeExists = nextExchange.exchangeTypeItems.some(x => x.id == previousExchange.exchangeTypeId)
      if (typeExists) {
        nextExchange.exchangeTypeId = previousExchange.exchangeTypeId
      }
    }
    if (previousExchange.languageId) {
      const languageExists = nextExchange.languageItems.some(x => x.id == previousExchange.languageId)
      if (languageExists) {
        nextExchange.languageId = previousExchange.languageId
      }
    }
    if (previousExchange.durationId) {
      const durationExists = nextExchange.durationItems.some(x => x.id == previousExchange.durationId)
      if (durationExists) {
        nextExchange.durationId = previousExchange.durationId
      }
    }
    nextExchange.preferredPeriod = previousExchange.preferredPeriod
    if (nextExchange.allowPreMatchedParticipantToBeNamed) {
      nextExchange.preMatchCounterpartName = previousExchange.preMatchCounterpartName
    }
    if (nextExchange.askAboutHostingMultipleCounterparts) {
      nextExchange.hasMultipleCounterparts = previousExchange.hasMultipleCounterparts
    }
  }
  return nextExchange
}

const selectExchange = (exchange: OspeExchangeChoice | null, exchangeIdx: number): void => {
  selectExchangeModal.value?.hideModal()

  if (shortTermApplication.value) {
    let previousExchange: OspeExchangeChoice | null = null
    let newExchange: OspeExchangeChoice | null = null

    if (exchange) {
      switch (exchangeIdx) {
        case 0:
          previousExchange = shortTermApplication.value.firstChoice
          break
        case 1:
          previousExchange = shortTermApplication.value.secondChoice
          break
        case 2:
          previousExchange = shortTermApplication.value.thirdChoice
          break
      }
      newExchange = prepareExchange(exchange, previousExchange)
    }

    switch (exchangeIdx) {
      case 0:
        shortTermApplication.value.firstChoice = newExchange
        break
      case 1:
        shortTermApplication.value.secondChoice = newExchange
        break
      case 2:
        shortTermApplication.value.thirdChoice = newExchange
        break
    }
  }
}

const onRemoveChoiceConfirmed = (exchangeIdx?: unknown): void => {
  removeChoiceConfirmationModal.value?.hide()

  if (shortTermApplication.value) {
    switch (exchangeIdx) {
      case 0:
        shortTermApplication.value.firstChoice = shortTermApplication.value.secondChoice
      case 1:
        shortTermApplication.value.secondChoice = shortTermApplication.value.thirdChoice
      case 2:
        shortTermApplication.value.thirdChoice = null
    }
  }
}
</script>
